import type { MarketId, TemplateView } from '@ps/isp-web-services-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isValidInPlayStatus } from '@swf/sports/utils/isValidInPlayStatus';

import GroupInPanel from '../GroupInPanel/GroupInPanel';

import styles from './CompoundMarketTemplatePanel.scss';
import TemplatePanelTitle from './TemplatePanelTitle';

interface Props {
  competitionGroup: TemplateView[][];
  marketIds: (MarketId | null)[];
  groupedOn: 'DATE' | 'COMPETITION';
  isNested: boolean;
  isFlush?: boolean;
}

const CompoundMarketTemplatePanel: React.FC<Props> = ({
  competitionGroup,
  groupedOn,
  marketIds,
  isNested,
  isFlush = false
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const marketId = marketIds.find(Boolean) ?? '';
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : -1)
  );
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const panelName = groupedOn === 'DATE' ? event.eventStartTime : competition.competitionName;

  const eventStatus = useEventStatus({
    inPlay: market?.inPlay,
    startTime: event.eventStartTime,
    canTurnInPlay: market?.offeredInPlay
  });

  return (
    <div
      className={classNames(styles.panel, {
        [styles.nestedPanel]: isNested,
        [styles.flush]: isFlush,
        [styles.inPlay]: isValidInPlayStatus(eventStatus)
      })}
      key={panelName}
    >
      <ExpandablePanel
        isOpenInitially
        isNested={isNested}
        title={
          <TemplatePanelTitle
            groupedOn={groupedOn}
            competition={competition}
            isNested={isNested}
            panelName={panelName}
          />
        }
      >
        {competitionGroup.map((groupInPanel) => (
          <GroupInPanel key={`${marketIds.join(',')}_panel}`} groupInPanel={groupInPanel} />
        ))}
      </ExpandablePanel>
    </div>
  );
};

export default CompoundMarketTemplatePanel;
